import CarDataConsolidator, { CM_BODY_COLOUR, CM_PRIMARY_ACCENT_COLOUR } from "./classes/CarDataConsolidator";
import type {
    AccentColourType,
    ExtraType,
    UpholsteryType,
    WheelType,
    PackType,
    PriceType,
    ColourType,
    ExtraSetItemType,
    FinanceOptionType,
    PackAvailabilityType,
} from "../../../common-deprecated/types/CommonTypes";
import type { BasketType } from "../types/BasketType";
import type { ItemsType } from "../../../common-deprecated/redux/typeHelpers";
import type { FinancePackConfigurablesType } from "../types/FinanceTypes";

type PackItemType = {
    translatedNames: {
        model: string;
        grade: string;
        bodyType: string;
    };
    colours: ColourType[];
    upholsteries: UpholsteryType[];
    packs: PackType[];
    extras: ExtraType[];
    loading: boolean;
};

type packItemsType = {
    availability: PackAvailabilityType;
    price: PriceType;
    optionalEquipment: ExtraType[];
    colours: ColourType[];
    upholsteries: (UpholsteryType | ExtraType)[];
    wheels: WheelType[];
    renderedEquipment: (UpholsteryType | ExtraType | WheelType)[];
    selectedEquipment: string[];
    totalPackContent: number;
    primaryAccentColour: AccentColourType | null;
    primaryAccentColours: AccentColourType[];
    secondaryAccentColour: AccentColourType | null;
    secondaryAccentColours: AccentColourType[];
};

export const shouldDisableforMonthlyRate = (pack: PackType, financeOption: FinanceOptionType): boolean => {
    return pack.standardOnMonthlyRateToggle && financeOption === "monthly";
};

export const getPackItems = (
    pack: PackType,
    basket: BasketType,
    itemsUpholsteries: UpholsteryType[],
    financeOption: FinanceOptionType,
): packItemsType => {
    const price = CarDataConsolidator.getPackAccessoryUpholsteryPrice(pack, itemsUpholsteries);
    const isAvailable = CarDataConsolidator.isAvailable(pack, basket.colour, basket.upholstery);
    const availability = isAvailable.available && !shouldDisableforMonthlyRate(pack, financeOption);

    let primaryAccentColour: AccentColourType | null;
    let secondaryAccentColour: AccentColourType | null;
    let primaryAccentColours: AccentColourType[];
    let secondaryAccentColours: AccentColourType[];
    let standardEquipment: ExtraType[];
    let standardBodyColourDependentExtraOptions: ExtraType[];
    let primaryAccentColourExtras: ExtraType[];
    let optionalEquipment: ExtraType[];

    // The following logic makes sure that when a pack is not available for a specific color, the pack will be disabled but the content won't be affected.
    // This is to make sure that there is always pack content shown, even though the pack is disabled
    const defaultBodyColourCombinations = CarDataConsolidator.getDefaultBodyColourCombination(pack);
    if (!availability && defaultBodyColourCombinations) {
        ({ primaryAccentColour, secondaryAccentColour } = defaultBodyColourCombinations);
        primaryAccentColours = CarDataConsolidator.getPrimaryAccentColours(
            defaultBodyColourCombinations.colourID,
            pack,
        );
        secondaryAccentColours = primaryAccentColour.accentColours;

        const defaultConfig = {
            colour: { id: defaultBodyColourCombinations.colourID },
            primaryAccentColour,
            secondaryAccentColour,
        };
        standardEquipment = CarDataConsolidator.getExtrasBasedOnColour(defaultConfig, pack.visualItems.standard);
        optionalEquipment = CarDataConsolidator.getExtrasBasedOnColour(defaultConfig, pack.visualItems.optional);
        standardBodyColourDependentExtraOptions = CarDataConsolidator.mapExtrasBasedOnAccentColour(
            pack.visualItems.standard,
            defaultConfig.colour,
            defaultConfig.primaryAccentColour,
            defaultConfig.secondaryAccentColour,
            CM_BODY_COLOUR,
        );
        primaryAccentColourExtras = CarDataConsolidator.mapExtrasBasedOnAccentColour(
            pack.equipment as ExtraType[],
            defaultConfig.colour,
            defaultConfig.primaryAccentColour,
            defaultConfig.secondaryAccentColour,
            CM_PRIMARY_ACCENT_COLOUR,
        );
    } else {
        // Even if the pack is enabled, it is possible that the basket.primaryAccentColour is not available in the pack (when the color is selected through another pack for instance).
        // There can only be one primary accent colour selected throughout packs; if there is a mismatch, this is handled in the pack selection logic
        primaryAccentColours = CarDataConsolidator.getPrimaryAccentColours(basket.colour.id, pack);
        primaryAccentColour =
            // Check if there is a basket primaryAccentColour and if it is available in the packs accentColours
            (basket.primaryAccentColour &&
                primaryAccentColours.find(
                    (accentColour) => basket.primaryAccentColour && accentColour.id === basket.primaryAccentColour.id,
                )) ||
            // If not, use the defaultBodyColourCombinations, again if available
            (defaultBodyColourCombinations && defaultBodyColourCombinations.primaryAccentColour);
        secondaryAccentColours = CarDataConsolidator.getSecondaryAccentColours(basket, pack);
        // Same goes for the secondary accent colour
        secondaryAccentColour =
            // Check if there is a basket secondaryAccentColour and if it is available in the packs accentColours
            (basket.secondaryAccentColour &&
                secondaryAccentColours.find(
                    (accentColour) =>
                        basket.secondaryAccentColour && accentColour.id === basket.secondaryAccentColour.id,
                )) ||
            // If not, use the defaultBodyColourCombinations, again if available
            (defaultBodyColourCombinations && defaultBodyColourCombinations.secondaryAccentColour);
        const extrasBasedOnColourconfig = {
            colour: basket.colour,
            primaryAccentColour,
            secondaryAccentColour,
        };
        standardEquipment = CarDataConsolidator.getExtrasBasedOnColour(
            extrasBasedOnColourconfig,
            pack.visualItems.standard,
        );
        optionalEquipment = CarDataConsolidator.getExtrasBasedOnColour(
            extrasBasedOnColourconfig,
            pack.visualItems.optional,
        );
        standardBodyColourDependentExtraOptions = CarDataConsolidator.mapExtrasBasedOnAccentColour(
            pack.visualItems.standard,
            basket.colour,
            primaryAccentColour,
            secondaryAccentColour,
            CM_BODY_COLOUR,
        );
        primaryAccentColourExtras = CarDataConsolidator.mapExtrasBasedOnAccentColour(
            pack.equipment as ExtraType[],
            basket.colour,
            primaryAccentColour,
            secondaryAccentColour,
            CM_PRIMARY_ACCENT_COLOUR,
        );
    }

    const { colours, wheels, upholsteries, equipmentUpholsteries } = pack.visualItems;

    const combinedStandardEquipment = [
        ...standardEquipment,
        ...standardBodyColourDependentExtraOptions,
        ...primaryAccentColourExtras,
    ].filter((item) => item.options.length === 0 && !item.isHidden);

    const packEnabled = !pack.standard && availability;

    const combinedUpholsteries: (UpholsteryType | ExtraType)[] = upholsteries.concat(
        equipmentUpholsteries.sort((a, b) => Number(a.index > b.index)) as any,
    );

    const renderedEquipment: (UpholsteryType | ExtraType | WheelType)[] = [...combinedStandardEquipment];

    // Show single upholsteries (including accessory upholsteries) and wheels as a pack equipment item instead of a list.
    if (combinedUpholsteries.length === 1) {
        renderedEquipment.push(upholsteries.length === 1 ? upholsteries[0] : equipmentUpholsteries[0]);
    }
    if (wheels.length === 1) renderedEquipment.push(wheels[0]);

    // Calculate the total pack content
    const totalPackContent =
        colours.length +
        combinedUpholsteries.length +
        wheels.length +
        combinedStandardEquipment.length +
        optionalEquipment.length;

    return {
        availability: { enabled: packEnabled, reason: isAvailable.reason },
        price,
        colours,
        upholsteries: combinedUpholsteries,
        selectedEquipment: [],
        wheels,
        renderedEquipment, // RenderedEquipment possibly contains a wheel or upholstery if there is only one.
        totalPackContent,
        optionalEquipment,
        primaryAccentColours,
        secondaryAccentColours,
        primaryAccentColour,
        secondaryAccentColour,
    };
};

type ExtraInformationType = {
    renderedPrice: PriceType;
    renderedImage: string;
    showAccessoryDisclaimer: boolean;
    setItems?: ExtraSetItemType[];
    includedItems?: ExtraType[];
};

export const getExtraInformation = (
    extra: ExtraType | WheelType | UpholsteryType,
    basket: BasketType,
    items: ItemsType | PackItemType,
    accessoryDisclaimerIsDefined: boolean,
): ExtraInformationType => {
    const renderedPrice = extra.price;
    const renderedImage = extra.image;
    let setItems;
    let includedItems;

    if (extra.objectType === "extra") {
        // Set item logic.
        // Only render the set-items which validate for the current colour, upholstery & colourMode config in the description tooltip
        setItems = CarDataConsolidator.getSetItems(
            extra,
            basket.colour,
            basket.upholstery,
            basket.primaryAccentColour,
            basket.secondaryAccentColour,
        );
        includedItems = CarDataConsolidator.getIncludedExtras(extra, items.extras);
    }
    const showAccessoryDisclaimer =
        (extra as ExtraType | WheelType).type === "accessory" && accessoryDisclaimerIsDefined && !!extra.description;

    return {
        renderedPrice,
        renderedImage,
        showAccessoryDisclaimer,
        setItems,
        includedItems,
    };
};

export const getFinancePackContent = (
    pack: PackType,
    isStandard: boolean,
    financeOption: FinanceOptionType,
    items: ItemsType,
): FinancePackConfigurablesType[] => {
    const packContent = [...pack.equipment, ...pack.serviceProducts];
    if (financeOption === "monthly") {
        return packContent.filter((equipment) => equipment.standard === isStandard);
    } else {
        return packContent
            .filter((content) => content.standard === isStandard)
            .map((content) => {
                if (items.extras.find((extra) => extra.id === content.id))
                    return items.extras.find((extra) => extra.id === content.id);
                if (items.serviceProducts.find((serviceProduct) => serviceProduct.id === content.id))
                    return items.serviceProducts.find((serviceProduct) => serviceProduct.id === content.id);
                if (items.wheels.find((wheel) => wheel.id === content.id))
                    return items.wheels.find((wheel) => wheel.id === content.id);
                else return null;
            })
            .filter((content) => content) as FinancePackConfigurablesType[];
    }
};

export const getSelectedFinancePackEquipment = (
    pack: PackType,
    basket: BasketType,
    items: ItemsType,
    financeOption: FinanceOptionType,
): { standard: FinancePackConfigurablesType[]; optional: FinancePackConfigurablesType[] } => {
    const filterFinancePackExtras = (
        financePackExtra: FinancePackConfigurablesType[],
    ): FinancePackConfigurablesType[] => {
        return financePackExtra.filter(
            (equipment) =>
                (financeOption === "cash" &&
                    (basket.extras.find((extra) => extra.id === equipment.id) ||
                        basket.serviceProducts.find((serviceProduct) => serviceProduct.id === equipment.id))) ||
                (financeOption === "monthly" &&
                    (basket.financePackExtras.find((extra) => extra.id === equipment.id) ||
                        basket.financePackServiceProducts.find(
                            (serviceProduct) => serviceProduct.id === equipment.id,
                        ))),
        );
    };

    const standardEquipment = filterFinancePackExtras(getFinancePackContent(pack, true, financeOption, items));
    const optionalEquipment = filterFinancePackExtras(getFinancePackContent(pack, false, financeOption, items));

    return {
        standard: standardEquipment,
        optional: optionalEquipment,
    };
};
